import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import {SessionStorageService} from 'ngx-webstorage';
import { AbstractSubscribeUnsubscribeDirective } from '@cupoui/shared/components/abstract-subscribe-unsubscribe-directive';
import { VERSION } from '../../app.constants';
import { LANGUAGES } from '@cupoui/core/language/language.constants';
import { AccountService } from '@cupoui/core/auth/account.service';
import { UserService } from '@cupoui/core/user/user.service';
import { Account } from '@cupoui/core/user/account.model';
import { UntilDestroy } from '@ngneat/until-destroy';
import { DisplayPageType, PageType } from '@cupoui/shared/components/info-bar/info-bar.component';
import { TranslateService } from '@ngx-translate/core';
import {AuthenticationService} from "@cupoui/core/auth/authentication.service";
import { LocaleService } from '@cupoui/core/services/locale.service';

@Component({
    selector: 'jhi-navbar',
    templateUrl: './navbar.component.html',
    styleUrls: ['./navbar.scss'],
    standalone: false
})
@UntilDestroy({ checkProperties: true })
export class NavbarComponent extends AbstractSubscribeUnsubscribeDirective implements OnInit {
  isNavbarCollapsed = true;
  languages = LANGUAGES;
  version: string;

  account: Account;

  avatarUrl: string | null;

  constructor(
    private translateService: TranslateService,
    private sessionStorage: SessionStorageService,
    private accountService: AccountService,
    private userService: UserService,
    public router: Router,
    private authenticationService: AuthenticationService,
    private localeService: LocaleService
  ) {
    super();
    if (VERSION.toLowerCase().startsWith('v')) {
      this.version = VERSION;
    } else {
      this.version = 'v' + VERSION;
    }
  }

  ngOnInit(): void {
    this.subscriptions.push(
      this.accountService.identity().subscribe(account => (account ? (this.account = account) : null)),
      this.userService.avatarImageChanged$.subscribe(changed => {
        this.getAvatarUrl(changed);
      })
    );
  }

  changeLanguage(languageKey: string): void {
    this.sessionStorage.store('locale', languageKey);
    this.localeService.setLocale(languageKey);
    this.translateService.use(languageKey);
    if (this.account) {
      this.account.langKey = languageKey;
      const subscription: Subscription = this.accountService.save(this.account).subscribe(() => {
        this.accountService.languageChanged(languageKey);
        subscription.unsubscribe();
      });
    }
  }

  collapseNavbar(): void {
    this.isNavbarCollapsed = true;
  }

  isAuthenticated(): boolean {
    return this.accountService.isAuthenticated();
  }

  logout(): void {
    this.collapseNavbar();
    localStorage.removeItem('selected-customer-profile-codes');
    localStorage.removeItem('selected-customer-profile-ids');
    localStorage.removeItem('selected-customer-role-names');
    this.sessionStorage.clear(DisplayPageType.DISPLAY_LAST_PAGE);
    this.subscriptions.push(
      this.authenticationService.logout().subscribe({
        error: err => console.error('Logout failed:', err)
      })
    );
  }

  toggleNavbar(): void {
    this.isNavbarCollapsed = !this.isNavbarCollapsed;
  }

  getFirstName(): string {
    if (!this.isAuthenticated()) {
      return '';
    } else {
      return this.accountService.getFirstName();
    }
  }

  getFullName(): string {
    if (!this.isAuthenticated()) {
      return '';
    } else {
      return this.accountService.getFullName();
    }
  }

  getAvatarUrl(forceRefresh = false): void {
    this.avatarUrl = this.isAuthenticated() ? this.userService.avatarUrl(this.accountService.getLogin(), forceRefresh) : '';
  }
}
